import { Query } from "@apollo/client/react/components";
import { Input } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React, { ChangeEvent, useState } from "react";
import GroupProjectsFromGovernments from "~/lib/queries/GroupProjectsFromGovernments";
import GroupProjectsPendingVerification from "~/lib/queries/GroupProjectsPendingVerification";
import {
  IGroupProjectsFromGovernmentsQuery,
  IGroupProjectsFromGovernmentsQueryVariables,
} from "~/lib/queries/__generated__/GroupProjectsFromGovernments.generated";
import {
  IGroupProjectsPendingVerificationQuery,
  IGroupProjectsPendingVerificationQueryVariables,
} from "~/lib/queries/__generated__/GroupProjectsPendingVerification.generated";
import { IGroupQuery } from "~/lib/queries/__generated__/GroupQuery.generated";

import CreateProjectModal from "~/src/components/project/CreateProjectModal";
import ProjectsListing from "~/src/components/project/listing/ProjectsListing";
import { IGroupVisibility } from "~/types/types";
import NoProjectsBox from "~/components/profile/NoProjectsBox";
import { Margin } from "~/components/spacings";
import NoResultsFound from "~/components/group/NoResultsFound";
import { useIntl, FormattedMessage } from "react-intl";
import { useRouter } from "next/router";

interface IProjectsProps {
  group: NonNullable<IGroupQuery["groupBySlug"]>;
}

export default function Projects({ group }: IProjectsProps) {
  const intl = useIntl();
  const router = useRouter();
  const [filter, setFilter] = useState("");
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  if (!group.projects) {
    return null;
  }

  const { items: projects, pageInfo } = group.projects;

  const filtered = projects!.filter((project) => {
    if (!filter || !project.title) {
      return true;
    }

    return project.title.toLowerCase().includes(filter.toLowerCase());
  });

  if (!projects || !pageInfo) {
    return null;
  }

  if (projects.length === 0) {
    return (
      <div
        data-intercom-target="Group Projects"
        css={{
          display: "grid",
          gridRowGap: 28,
        }}
      >
        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          groupId={group._id}
          onClose={() => setIsCreateProjectModalOpen(false)}
        />

        <NoProjectsBox
          title={
            group.visibility === IGroupVisibility.Public
              ? intl.formatMessage({
                  defaultMessage: "Share a topic to discuss",
                  id: "wId4Ci",
                })
              : undefined
          }
          text={
            group.visibility === IGroupVisibility.Public
              ? intl.formatMessage({
                  defaultMessage:
                    "Share what you’re working on or ask a question. Adding a topic takes less than a minute!",
                  id: "77NhtG",
                })
              : undefined
          }
          buttonText={
            group.visibility === IGroupVisibility.Public
              ? intl.formatMessage({
                  defaultMessage: "Add Topic",
                  id: "UJQ8Fy",
                })
              : undefined
          }
          onAddProject={() => setIsCreateProjectModalOpen(true)}
          groupId={group._id}
        />

        <ProjectsPendingVerification group={group} />

        <ProjectsFromOtherGovernments group={group} />
      </div>
    );
  }

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 28,
      }}
    >
      <div
        css={{
          borderRadius: 5,
          border: `1px solid ${palette.lightestGray}`,
          background: "#fff",
        }}
      >
        <div
          css={{
            padding: 16,
          }}
        >
          <Margin mb={16}>
            <Input
              placeholder={
                group.visibility === IGroupVisibility.Public
                  ? intl.formatMessage({
                      defaultMessage: "Search for topics...",
                      id: "hj9egu",
                    })
                  : intl.formatMessage({
                      defaultMessage: "Search for projects...",
                      id: "aobebN",
                    })
              }
              value={filter}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
            />
          </Margin>

          <div>
            {filtered.length === 0 && filter.length > 0 && <NoResultsFound />}

            {filtered.length > 0 && (
              <ProjectsListing
                addProjectSlotText={
                  group.visibility === IGroupVisibility.Public
                    ? intl.formatMessage({
                        defaultMessage: "Add another topic",
                        id: "16zteO",
                      })
                    : undefined
                }
                projects={filtered}
                onModalClose={() => {
                  router.push(`/groups/${group.slug}?content=projects`);
                }}
                gridProps={{ columns: [1, 1, 2], spacingY: 6 }}
                withAddProjectSlot={filter.length === 0 && group.viewerIsMember}
                groupId={group._id}
              />
            )}
          </div>
        </div>
      </div>

      <ProjectsPendingVerification group={group} />

      <ProjectsFromOtherGovernments group={group} />
    </div>
  );
}

interface IProjectsPendingVerificationProps {
  group: NonNullable<IGroupQuery["groupBySlug"]>;
}

function ProjectsPendingVerification({ group }: IProjectsPendingVerificationProps) {
  const intl = useIntl();
  const router = useRouter();

  return (
    <Query<IGroupProjectsPendingVerificationQuery, IGroupProjectsPendingVerificationQueryVariables>
      query={GroupProjectsPendingVerification}
      variables={{
        slug: group.slug,
        cursor: null,
      }}
    >
      {({ data, fetchMore }) => {
        if (!data || !data.groupBySlug) {
          return null;
        }

        const { projectsPendingVerification } = data.groupBySlug;

        if (!projectsPendingVerification.items || projectsPendingVerification.items.length === 0) {
          return null;
        }

        return (
          <div>
            <h2
              css={{
                margin: "0 0 16px 0",
                color: palette.sealBlue,
                fontWeight: 400,
                fontSize: 16,
              }}
            >
              <FormattedMessage defaultMessage="Pending Verification" id="V5sK5b" />
            </h2>

            <ProjectsListing
              projects={projectsPendingVerification.items}
              addProjectSlotText={
                group.visibility === IGroupVisibility.Public
                  ? intl.formatMessage({
                      defaultMessage: "Add another topic",
                      id: "16zteO",
                    })
                  : undefined
              }
              onModalClose={() => {
                router.push(`/groups/${group.slug}?content=projects`);
              }}
              gridProps={{ columns: [1, 1, 2], spacingY: 6 }}
              hasNextPage={projectsPendingVerification.pageInfo?.hasNextPage || false}
              onLoadMore={() => {
                if (projectsPendingVerification.pageInfo && projectsPendingVerification.pageInfo.endCursor) {
                  fetchMore({
                    variables: {
                      slug: group.slug,
                      cursor: projectsPendingVerification.pageInfo.endCursor,
                    },
                    updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                      if (
                        !previousQueryResult.groupBySlug ||
                        !previousQueryResult.groupBySlug.projectsPendingVerification.items ||
                        !fetchMoreResult ||
                        !fetchMoreResult.groupBySlug ||
                        !fetchMoreResult.groupBySlug.projectsPendingVerification ||
                        !fetchMoreResult.groupBySlug.projectsPendingVerification.items
                      ) {
                        return previousQueryResult;
                      }

                      return {
                        ...previousQueryResult,
                        groupBySlug: {
                          ...previousQueryResult.groupBySlug,
                          projectsPendingVerification: {
                            ...fetchMoreResult.groupBySlug.projectsPendingVerification,
                            items: [
                              ...previousQueryResult.groupBySlug.projectsPendingVerification.items,
                              ...fetchMoreResult.groupBySlug.projectsPendingVerification.items,
                            ],
                          },
                        },
                      };
                    },
                  });
                }
              }}
            />
          </div>
        );
      }}
    </Query>
  );
}

interface IProjectsFromOtherGovernmentsProps {
  group: NonNullable<IGroupQuery["groupBySlug"]>;
}

function ProjectsFromOtherGovernments({ group }: IProjectsFromOtherGovernmentsProps) {
  const intl = useIntl();
  const router = useRouter();

  return (
    <Query<IGroupProjectsFromGovernmentsQuery, IGroupProjectsFromGovernmentsQueryVariables>
      query={GroupProjectsFromGovernments}
      variables={{
        slug: group.slug,
        cursor: null,
      }}
      notifyOnNetworkStatusChange={true}
    >
      {({ data, loading, fetchMore }) => {
        if (!data || !data.groupBySlug) {
          return null;
        }

        const { projectsFromGovernments } = data.groupBySlug;

        if (!projectsFromGovernments.items || projectsFromGovernments.items.length === 0) {
          return null;
        }

        return (
          <div
            css={{
              display: "grid",
              gridRowGap: 24,
            }}
          >
            <div>
              <h2
                css={{
                  margin: "0 0 16px 0",
                  color: palette.sealBlue,
                  fontWeight: 400,
                  fontSize: 16,
                }}
              >
                <FormattedMessage defaultMessage="Shared projects from Governments" id="6TuHLI" />
              </h2>

              <ProjectsListing
                onModalClose={() => {
                  router.push(`/groups/${group.slug}?content=projects`);
                }}
                addProjectSlotText={
                  group.visibility === IGroupVisibility.Public
                    ? intl.formatMessage({
                        defaultMessage: "Add another topic",
                        id: "16zteO",
                      })
                    : undefined
                }
                projects={projectsFromGovernments.items}
                gridProps={{ columns: [1, 1, 2], spacingY: 6 }}
                hasNextPage={projectsFromGovernments.pageInfo?.hasNextPage || false}
                loading={loading}
                onLoadMore={() => {
                  if (projectsFromGovernments.pageInfo && projectsFromGovernments.pageInfo.endCursor) {
                    fetchMore({
                      variables: {
                        slug: group.slug,
                        cursor: projectsFromGovernments.pageInfo.endCursor,
                      },
                      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                        if (
                          !previousQueryResult.groupBySlug ||
                          !previousQueryResult.groupBySlug.projectsFromGovernments.items ||
                          !fetchMoreResult ||
                          !fetchMoreResult.groupBySlug ||
                          !fetchMoreResult.groupBySlug.projectsFromGovernments ||
                          !fetchMoreResult.groupBySlug.projectsFromGovernments.items
                        ) {
                          return previousQueryResult;
                        }

                        return {
                          ...previousQueryResult,
                          groupBySlug: {
                            ...previousQueryResult.groupBySlug,
                            projectsFromGovernments: {
                              ...fetchMoreResult.groupBySlug.projectsFromGovernments,
                              items: [
                                ...previousQueryResult.groupBySlug.projectsFromGovernments.items,
                                ...fetchMoreResult.groupBySlug.projectsFromGovernments.items,
                              ],
                            },
                          },
                        };
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        );
      }}
    </Query>
  );
}
